import { useState, useEffect, useCallback, useRef } from "react";
import { X, MapPin } from "lucide-react";
import { Story } from "@/hooks/useStories";
import { supabase } from "@/integrations/supabase/client";

interface StoryViewerProps {
  stories: Story[];
  initialIndex: number;
  onClose: () => void;
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const diff = now - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function StoryViewer({ stories, initialIndex, onClose }: StoryViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const touchStartX = useRef(0);
  const DURATION = 5000; // 5 seconds per story

  const story = stories[currentIndex];

  const goNext = useCallback(() => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex((i) => i + 1);
      setProgress(0);
    } else {
      onClose();
    }
  }, [currentIndex, stories.length, onClose]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
      setProgress(0);
    }
  }, [currentIndex]);

  // Increment view count
  useEffect(() => {
    supabase
      .from("stories" as any)
      .update({ view_count: (story.view_count || 0) + 1 } as any)
      .eq("id", story.id)
      .then();
  }, [story.id]);

  // Auto-advance timer for images
  useEffect(() => {
    if (story.media_type === "video") return;

    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min((elapsed / DURATION) * 100, 100);
      setProgress(pct);
      if (pct >= 100) {
        goNext();
      }
    }, 50);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [currentIndex, story.media_type, goNext]);

  // Video progress handler
  useEffect(() => {
    if (story.media_type !== "video" || !videoRef.current) return;

    const video = videoRef.current;
    const onTimeUpdate = () => {
      if (video.duration) {
        setProgress((video.currentTime / video.duration) * 100);
      }
    };
    const onEnded = () => goNext();

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("ended", onEnded);
    video.play().catch(() => {});

    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("ended", onEnded);
    };
  }, [currentIndex, story.media_type, goNext]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, goNext, goPrev]);

  // Tap left/right navigation
  const handleTap = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < rect.width / 3) {
      goPrev();
    } else {
      goNext();
    }
  };

  // Swipe support
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
      onClick={handleTap}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Progress bars */}
      <div className="absolute top-0 left-0 right-0 flex gap-1 p-2 pt-safe z-10">
        {stories.map((_, i) => (
          <div key={i} className="flex-1 h-[3px] bg-white/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-100"
              style={{
                width:
                  i < currentIndex
                    ? "100%"
                    : i === currentIndex
                    ? `${progress}%`
                    : "0%",
              }}
            />
          </div>
        ))}
      </div>

      {/* Close button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-10 right-4 z-20 text-white/80 hover:text-white p-2"
        aria-label="Close stories"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Time ago */}
      <span className="absolute top-10 left-4 z-20 text-white/70 text-xs">
        {timeAgo(story.created_at)}
      </span>

      {/* Story media */}
      <div className="w-full h-full flex items-center justify-center">
        {story.media_type === "video" && story.video_url ? (
          <video
            ref={videoRef}
            src={story.video_url}
            className="max-w-full max-h-full object-contain"
            playsInline
            muted={false}
          />
        ) : (
          <img
            src={story.image_url}
            alt={story.caption || "Job story"}
            className="max-w-full max-h-full object-contain"
          />
        )}
      </div>

      {/* Bottom overlay: caption + location */}
      {(story.caption || story.location_tag) && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 pb-safe z-10">
          {story.location_tag && (
            <div className="flex items-center gap-1 text-white/80 text-xs mb-1">
              <MapPin className="w-3 h-3" />
              <span>{story.location_tag}</span>
            </div>
          )}
          {story.caption && (
            <p className="text-white text-sm font-medium">{story.caption}</p>
          )}
        </div>
      )}
    </div>
  );
}
